import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import styles from './SortablePhotoList.module.scss';

export interface ISortablePhoto {
  id: number;
  full_photo_url: string;
}

interface SortablePhotoItemProps {
  photo: ISortablePhoto;
  onRemove?: (id: number) => void;
  canEdit: boolean;
}

const SortablePhotoItem = ({ photo, onRemove, canEdit }: SortablePhotoItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: photo.id,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: canEdit ? 'grab' : 'default',
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.photoItem} {...attributes} {...listeners}>
      <img className={styles.image} src={photo.full_photo_url} alt="" draggable={false} />
      {canEdit && onRemove && (
        <button
          type="button"
          className={styles.removeBtn}
          onClick={e => { e.stopPropagation(); onRemove(photo.id); }}
          onPointerDown={e => e.stopPropagation()}
        >
          <CloseIcon className={styles.closeIcon} />
        </button>
      )}
    </div>
  );
};

interface SortablePhotoListProps {
  photos: ISortablePhoto[];
  onReorder: (photos: ISortablePhoto[]) => void;
  onRemove?: (id: number) => void;
  canEdit?: boolean;
}

const SortablePhotoList = ({
  photos,
  onReorder,
  onRemove,
  canEdit = true,
}: SortablePhotoListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = photos.findIndex(p => p.id === active.id);
    const newIndex = photos.findIndex(p => p.id === over.id);
    onReorder(arrayMove(photos, oldIndex, newIndex));
  };

  if (!photos.length) return null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={photos.map(p => p.id)} strategy={horizontalListSortingStrategy}>
        <ul className={styles.list}>
          {photos.map(photo => (
            <SortablePhotoItem
              key={photo.id}
              photo={photo}
              onRemove={onRemove}
              canEdit={canEdit}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

export default SortablePhotoList;
